import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AlertCircle, Zap, Heart, Clock, FileSpreadsheet, Download, Loader2 } from "lucide-react";
import { useTranslation, useLocale } from "../contexts/LocaleContext";
import { getUserData } from "../utils/auth";
import { authenticatedFetch } from "../config/api";
import JSZip from "jszip";

interface TrialExpiredContentProps {
  onSubscribe: (plan: "monthly" | "yearly" | "lifetime") => void;
  isPage?: boolean;
}

export default function TrialExpiredContent({
  onSubscribe,
  isPage = false,
}: TrialExpiredContentProps) {
  const t = useTranslation();
  const { locale } = useLocale();
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [isDownloadingInvoices, setIsDownloadingInvoices] = useState(false);
  const [isDownloadingExpenses, setIsDownloadingExpenses] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const userData = getUserData();
      if (!userData || !userData.createdAt) return 0;

      const registrationDate = new Date(userData.createdAt);
      if (isNaN(registrationDate.getTime())) return 0;

      const trialDays = (userData.trial_days_amount != null && userData.trial_days_amount > 0)
        ? userData.trial_days_amount
        : 30;
      const offerEndDays = trialDays + 2;
      const offerEndTime =
        registrationDate.getTime() + offerEndDays * 24 * 60 * 60 * 1000;
      const now = Date.now();
      const remaining = Math.floor((offerEndTime - now) / 1000);

      return Math.max(0, remaining);
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, "0")}:${m
      .toString()
      .padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const handleDownloadInvoices = async () => {
    try {
      setIsDownloadingInvoices(true);
      const response = await authenticatedFetch("/api/invoices-full");
      if (!response.ok) throw new Error("Failed to fetch invoices");
      
      const invoices = await response.json();
      if (!invoices || invoices.length === 0) {
        alert(t.invoiceList?.noInvoices || "No invoices found");
        return;
      }

      const zip = new JSZip();
      let successCount = 0;

      // Import translations dynamically
      const { invoiceTranslations } = await import("../translations/invoiceTranslations");

      for (const invoice of invoices) {
        try {
          if (!invoice.internalId) continue;

          const invResponse = await authenticatedFetch(`/api/invoices/${invoice.internalId}`);
          if (!invResponse.ok) continue;

          const fullInvoiceData = await invResponse.json();
          const activeVersion = fullInvoiceData.versions?.find((v: any) => v.metadata?.isActive || v.isActive);
          if (!activeVersion) continue;

          const { draft, designSettings, language } = activeVersion;
          const lang = (language || "en") as keyof typeof invoiceTranslations;
          const translations = invoiceTranslations[lang] || invoiceTranslations.en;

          const sanitizeFilename = (filename: string) => {
            return filename
              .toLowerCase()
              .replace(/[^a-z0-9\-_]/g, "-")
              .replace(/-+/g, "-")
              .replace(/^-|-$/g, "");
          };
          const filename = `invoice-${sanitizeFilename(draft.invoiceNo)}.pdf`;

          const pdfResponse = await authenticatedFetch("/generate-pdf", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              draft,
              translations,
              fontFamily: designSettings?.fontFamily || "Inter, Arial, sans-serif",
              designSettings,
            }),
          });

          if (pdfResponse.ok) {
            const pdfBlob = await pdfResponse.blob();
            zip.file(filename, pdfBlob);
            successCount++;
          }
        } catch (err) {
          console.error("Error processing invoice:", err);
        }
      }

      if (successCount > 0) {
        const content = await zip.generateAsync({ type: "blob" });
        const url = window.URL.createObjectURL(content);
        const a = document.createElement("a");
        a.href = url;
        a.download = `invoices-${new Date().toISOString().split("T")[0]}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert("Failed to generate any PDFs");
      }
    } catch (error) {
      console.error("Error downloading invoices:", error);
      alert("An error occurred during download");
    } finally {
      setIsDownloadingInvoices(false);
    }
  };

  const handleDownloadExpenses = async () => {
    try {
      setIsDownloadingExpenses(true);
      const url = `/api/export-expenses-xlsx?lang=${locale}`;
      const response = await authenticatedFetch(url);
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `expenses-export-${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      } else {
        const errorData = await response.json();
        alert(errorData.error || "Failed to export expenses");
      }
    } catch (error) {
      console.error("Error exporting expenses:", error);
      alert("An error occurred during export");
    } finally {
      setIsDownloadingExpenses(false);
    }
  };

  const containerClasses = isPage
    ? "relative w-full max-w-xl mx-auto bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-xl overflow-hidden border border-slate-200 dark:border-slate-800 my-4"
    : "relative w-full max-w-xl bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 max-h-[95vh] overflow-y-auto";

  return (
    <div className={containerClasses}>
      {/* Top decorative gradient */}
      <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-rose-500 via-purple-600 to-indigo-600" />

      <div className="p-5 md:py-5 md:px-10">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-4 md:mb-5">
          <h2 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white mb-1 tracking-tight">
            {t.userSettings.trialExpired.title}
          </h2>
          <p className="text-sm md:text-sm text-slate-600 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
            {t.userSettings.trialExpired.description}
          </p>
        </div>

        {/* Pricing Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-4 md:mb-5">
          {/* Monthly */}
          <motion.div
            whileHover={{ y: -3 }}
            className="relative p-5 md:p-6 rounded-[2rem] bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 flex flex-col items-center text-center group transition-colors hover:border-purple-200 dark:hover:border-purple-900 shadow-sm"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 py-1 px-3 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-[9px] font-black uppercase tracking-wider shadow-sm border border-slate-100 dark:border-slate-600 whitespace-nowrap">
              {t.landing.pricing.mostPopular}
            </div>
            <span className="text-[10px] md:text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1 mt-1">
              {t.userSettings.trialExpired.monthly}
            </span>
            <div className="flex items-baseline gap-1 mb-3 md:mb-4">
              <span className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white">
                {t.landing.pricing.monthlyPrice}
              </span>
              <span className="text-slate-500 dark:text-slate-400 text-xs md:text-sm font-medium">
                {t.userSettings.trialExpired.periodMonth}
              </span>
            </div>
            <button
              onClick={() => onSubscribe("monthly")}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-br from-purple-600/90 to-indigo-700/90 text-white font-black text-sm md:text-base shadow-lg hover:from-purple-600 hover:to-indigo-700 transition-all duration-300 transform active:scale-95"
            >
              <span>{t.userSettings.trialExpired.subscribe}</span>
              <Zap size={14} className="fill-current" />
            </button>
          </motion.div>

          {/* Yearly */}
          <motion.div
            whileHover={{ y: -3 }}
            className="relative p-5 md:p-6 rounded-[2rem] bg-gradient-to-br from-purple-600 to-indigo-700 text-white flex flex-col items-center text-center shadow-xl shadow-indigo-500/25"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 py-1 px-4 rounded-full bg-[#f1c40f] text-slate-900 text-[10px] font-black uppercase tracking-widest shadow-[0_4px_12px_rgba(241,196,15,0.3)] border border-white/20 whitespace-nowrap">
              {t.landing.pricing.beneficial}
            </div>
            <span className="text-[10px] md:text-[11px] font-bold text-indigo-100 uppercase tracking-widest mb-1 mt-1">
              {t.userSettings.trialExpired.yearly}
            </span>
            <div className="flex items-baseline gap-1 mb-3 md:mb-4">
              <span className="text-2xl md:text-3xl font-black">
                {t.landing.pricing.oneYearPrice}
              </span>
              <span className="text-indigo-100 text-xs md:text-sm font-medium">
                {t.userSettings.trialExpired.periodYear}
              </span>
            </div>
            <button
              onClick={() => onSubscribe("yearly")}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white text-indigo-700 font-black text-sm md:text-base shadow-xl hover:bg-indigo-50 transition-all duration-300 transform active:scale-95"
            >
              <span>{t.userSettings.trialExpired.subscribe}</span>
              <Zap size={14} className="fill-current" />
            </button>
          </motion.div>
        </div>

        {/* Lifetime Offer - commented out as requested
        {timeLeft !== null && timeLeft > 0 && (
          <div className="bg-slate-50 dark:bg-slate-800/30 rounded-[1.5rem] p-5 border border-slate-100 dark:border-slate-800 text-center relative overflow-hidden group">
            <div className="relative z-10">
              <div className="text-[13px] text-slate-700 dark:text-slate-300 mb-4 leading-relaxed px-2">
                <span className="font-black text-slate-900 dark:text-white block mb-2 text-sm md:text-base">
                  {t.userSettings.trialExpired.lifetimeOffer.split("?")[0]}?
                </span>
                
                <div className="flex flex-col gap-3 items-center">
                  <p className="max-w-md">
                    {t.userSettings.trialExpired.lifetimeAccess}{" "}
                    <span className="font-black text-slate-900 dark:text-white">
                      {t.landing.pricing.lifetimePrice}
                    </span>.
                  </p>
                  
                  <div className="flex items-center gap-2 py-1.5 px-4 rounded-full bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/50 shadow-sm">
                    <span className="text-[10px] font-black text-amber-700 dark:text-amber-400 uppercase tracking-wider">
                      {t.userSettings.trialExpired.lifetimeLimited}
                    </span>
                    <div className="w-px h-3 bg-amber-200 dark:bg-amber-800" />
                    <span className="flex items-center gap-1.5 font-mono font-bold text-amber-600 dark:text-amber-400">
                      <Clock size={12} className="animate-pulse" />
                      {formatTime(timeLeft)}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => onSubscribe("lifetime")}
                className="inline-flex items-center justify-center gap-2 py-2.5 px-8 rounded-xl bg-rose-600 text-white font-black text-sm hover:bg-rose-700 transition-all duration-300 transform active:scale-95 group/btn shadow-lg shadow-rose-500/20"
              >
                <span>{t.userSettings.trialExpired.buyNow}</span>
                <Zap
                  size={14}
                  className="fill-current group-hover/btn:scale-110 transition-transform"
                />
              </button>
            </div>
          </div>
        )} */}

        {/* Download Previously Created Invoices and Expenses */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center border-t border-slate-100 dark:border-slate-800 pt-6">
          <button
            onClick={handleDownloadInvoices}
            disabled={isDownloadingInvoices || isDownloadingExpenses}
            className="flex items-center justify-center gap-2 px-5 py-3 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-2xl font-bold shadow-sm transition-all hover:bg-slate-50 dark:hover:bg-slate-700 text-xs disabled:opacity-50"
          >
            {isDownloadingInvoices ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Download size={16} className="text-indigo-500" />
            )}
            {t.userSettings.trialExpired.downloadPreviousInvoices}
          </button>
          <button
            onClick={handleDownloadExpenses}
            disabled={isDownloadingInvoices || isDownloadingExpenses}
            className="flex items-center justify-center gap-2 px-5 py-3 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-2xl font-bold shadow-sm transition-all hover:bg-slate-50 dark:hover:bg-slate-700 text-xs disabled:opacity-50"
          >
            {isDownloadingExpenses ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <FileSpreadsheet size={16} className="text-emerald-500" />
            )}
            {t.userSettings.trialExpired.downloadExpensesTable}
          </button>
        </div>

        {/* Footer */}
        <div className="mt-5 md:mt-6 flex flex-col items-center gap-2 text-center">
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 italic text-xs md:text-xs">
            <Heart size={14} className="text-rose-500 fill-rose-500" />
            <span>{t.userSettings.trialExpired.thanks}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

